import { makeObservable, observable, action, runInAction, reaction } from 'mobx';
import { sessionHelper } from '@/auth/sessionHelper';
import { performLogin, performLogout } from '@/auth/oauth';
import { setTelemetryUser, trackEvent } from '@/lib/telemetry';
import type { LoggedInUser } from '@/api/types';
import type { RootStore } from './RootStore';

export class AuthStore {
  @observable user: LoggedInUser | null = null;
  @observable isLoggingIn = false;
  @observable loginError: string | null = null;

  constructor(private root: RootStore) {
    makeObservable(this);
    // Hydrate from session if present so reload doesn't bounce to /login.
    this.user = sessionHelper.getUser();
    // Tag every telemetry event with the current user once known, and clear
    // when they log out. Reaction (not autorun) so we get clean change events.
    reaction(
      () => this.user?.userId ?? null,
      (userId) => setTelemetryUser(userId),
      { fireImmediately: true }
    );
  }

  get isAuthenticated(): boolean {
    return sessionHelper.isAuthenticated() && this.user !== null;
  }

  @action
  async login(username: string, password: string, rememberMe = false): Promise<void> {
    this.isLoggingIn = true;
    this.loginError = null;
    try {
      const user = await performLogin(username, password, rememberMe);
      runInAction(() => {
        this.user = user;
        this.isLoggingIn = false;
      });
      trackEvent('Auth.LoginSucceeded', { rememberMe });
    } catch (err) {
      runInAction(() => {
        this.isLoggingIn = false;
        this.loginError = parseLoginError(err);
      });
      trackEvent('Auth.LoginFailed', { reason: this.loginError ?? 'unknown' });
      throw err;
    }
  }

  @action
  logout(): void {
    trackEvent('Auth.Logout');
    this.user = null;
    // Clear merchant + downstream state so the next login starts fresh.
    this.root.merchantContextStore.reset();
    performLogout();
  }
}

function parseLoginError(err: unknown): string {
  if (typeof err === 'object' && err !== null) {
    const e = err as { response?: { data?: { error_description?: string; error?: string } }; message?: string };
    return (
      e.response?.data?.error_description ??
      e.response?.data?.error ??
      e.message ??
      'Login failed. Please try again.'
    );
  }
  return 'Login failed. Please try again.';
}
